/**
 * simulation.js
 * Monte Carlo discrete-event simulation engine for reliability/availability analysis
 */

import {
  generateFailureTime,
  generateRepairTime,
  SeededRandom,
  exponentialReliability,
} from './distributions.js';

// Event types
const EVENT_TYPES = {
  FAILURE: 'FAILURE',
  REPAIR_START: 'REPAIR_START',
  REPAIR_COMPLETE: 'REPAIR_COMPLETE',
  PM_DUE: 'PM_DUE',
  PM_START: 'PM_START',
  PM_COMPLETE: 'PM_COMPLETE',
  PHASE_CHANGE: 'PHASE_CHANGE',
  SPARE_ARRIVAL: 'SPARE_ARRIVAL',
  LABOR_RELEASE: 'LABOR_RELEASE',
};

/**
 * Priority queue for discrete event simulation
 */
class EventQueue {
  constructor() {
    this.events = [];
  }

  push(event) {
    this.events.push(event);
    this.events.sort((a, b) => a.time - b.time);
  }

  pop() {
    return this.events.shift();
  }

  isEmpty() {
    return this.events.length === 0;
  }

  clear() {
    this.events = [];
  }

  size() {
    return this.events.length;
  }
}

/**
 * Repair queue with priority support
 */
class RepairQueue {
  constructor() {
    this.queue = [];
  }

  push(task) {
    this.queue.push(task);
    // Sort by priority (lower number = higher priority), then by arrival time
    this.queue.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.arrivalTime - b.arrivalTime;
    });
  }

  pop() {
    return this.queue.shift();
  }

  isEmpty() {
    return this.queue.length === 0;
  }

  size() {
    return this.queue.length;
  }
}

/**
 * Main simulation engine
 */
export class SimulationEngine {
  constructor(projectData, config = {}) {
    this.projectData = projectData;
    this.config = {
      iterations: config.iterations || 10000,
      missionTime: config.missionTime || 8760, // hours (1 year)
      seed: config.seed || 12345,
      convergenceThreshold: config.convergenceThreshold || 0.01,
      numIntervals: config.numIntervals || 12,
      discountRate: config.discountRate || 0.05,
      costEscalation: config.costEscalation || 0.03,
      enableResourceConstraints: config.enableResourceConstraints || false,
      convergenceCheckInterval: config.convergenceCheckInterval || 100,
    };

    this.running = false;
    this.componentResults = new Map();
    this.systemResults = {
      systemAvailability: 0,
      systemMTBF: 0,
      systemMTTR: 0,
      totalCost: 0,
      costBreakdown: {
        labor: 0,
        spares: 0,
        consequence: 0,
        pm: 0,
        downtime: 0,
      },
      componentResults: [],
      importanceMeasures: [],
      profileData: [],
      resourceResults: null,
      convergedAt: null,
      confidenceIntervals: {},
      productionResults: {},
      systemCapacityUptime: 0,
    };

    this.rng = new SeededRandom(this.config.seed);
    this.stopped = false;
  }

  /**
   * Main simulation runner
   */
  async run(onProgress = null) {
    this.running = true;
    this.stopped = false;

    // Initialize tracking arrays
    const systemUptimes = [];
    const systemMTBFs = [];
    const systemMTTRs = [];
    const systemDowntimes = [];
    const systemCosts = [];
    const systemCapacityUptimes = [];
    const costsByCategory = {
      labor: [],
      spares: [],
      consequence: [],
      pm: [],
      downtime: [],
    };
    const productionByProduct = {}; // productName -> {throughput: [], shortfall: []}

    // Per-component tracking
    const compData = new Map();
    if (this.projectData.components) {
      for (const comp of this.projectData.components) {
        compData.set(comp.id, {
          failures: 0,
          repairs: 0,
          totalUptime: 0,
          totalDowntime: 0,
          totalCost: 0,
          costBreakdown: { labor: 0, spares: 0, consequence: 0, pm: 0 },
          failureTimes: [],
          downstarts: [],
          downends: [],
        });
      }
    }

    // Resource tracking across iterations (for averaging)
    let resourceResultsAccum = null;

    // Convergence tracking
    let convergedAtIter = null;
    const availabilityHistory = [];

    // Run iterations
    for (let iter = 0; iter < this.config.iterations; iter++) {
      if (this.stopped) break;

      const iterResult = this._runIteration();

      systemUptimes.push(iterResult.uptime);
      systemMTBFs.push(iterResult.mtbf);
      systemMTTRs.push(iterResult.mttr);
      systemDowntimes.push(iterResult.downtime);
      systemCosts.push(iterResult.totalCost);
      systemCapacityUptimes.push(iterResult.capacityUptime || 0);
      costsByCategory.labor.push(iterResult.costs.labor);
      costsByCategory.spares.push(iterResult.costs.spares);
      costsByCategory.consequence.push(iterResult.costs.consequence);
      costsByCategory.pm.push(iterResult.costs.pm);
      costsByCategory.downtime.push(iterResult.costs.downtime);

      // Accumulate production data
      if (iterResult.production) {
        for (const [productName, data] of Object.entries(iterResult.production)) {
          if (!productionByProduct[productName]) {
            productionByProduct[productName] = { throughput: [], shortfall: [] };
          }
          productionByProduct[productName].throughput.push(data.throughput || 0);
          productionByProduct[productName].shortfall.push(data.shortfall || 0);
        }
      }

      // Accumulate component data
      for (const [compId, compIter] of Object.entries(iterResult.components)) {
        if (compData.has(compId)) {
          const cd = compData.get(compId);
          cd.failures += compIter.failures;
          cd.repairs += compIter.repairs;
          cd.totalUptime += compIter.uptime;
          cd.totalDowntime += compIter.downtime;
          cd.totalCost += compIter.cost;
          cd.costBreakdown.labor += compIter.costs.labor;
          cd.costBreakdown.spares += compIter.costs.spares;
          cd.costBreakdown.consequence += compIter.costs.consequence;
          cd.costBreakdown.pm += compIter.costs.pm;
          cd.failureTimes.push(...compIter.failureTimes);
          cd.downstarts.push(...compIter.downstarts);
          cd.downends.push(...compIter.downends);
        }
      }

      // Accumulate resource results if enabled
      if (this.config.enableResourceConstraints && iterResult.resourceResults) {
        resourceResultsAccum = this._accumulateResourceResults(resourceResultsAccum, iterResult.resourceResults);
      }

      // Convergence check every N iterations
      if (convergedAtIter === null && (iter + 1) % this.config.convergenceCheckInterval === 0) {
        const currentAvailability = systemUptimes.reduce((a, b) => a + b, 0) / systemUptimes.length / this.config.missionTime;
        availabilityHistory.push(currentAvailability);

        if (availabilityHistory.length >= 2) {
          const prev = availabilityHistory[availabilityHistory.length - 2];
          const curr = availabilityHistory[availabilityHistory.length - 1];
          if (Math.abs(curr - prev) < this.config.convergenceThreshold) {
            convergedAtIter = iter + 1;
          }
        }
      }

      // Progress callback
      if (onProgress && iter % Math.max(1, Math.floor(this.config.iterations / 100)) === 0) {
        onProgress((iter / this.config.iterations) * 100);
      }
    }

    if (this.stopped) {
      this.running = false;
      return null;
    }

    // Aggregate results
    const n = convergedAtIter || this.config.iterations;

    const avgUptime = systemUptimes.slice(0, n).reduce((a, b) => a + b, 0) / n;
    const _avgDowntime = systemDowntimes.slice(0, n).reduce((a, b) => a + b, 0) / n;
    const systemAvailability = avgUptime / this.config.missionTime;
    const avgMTBF = systemMTBFs.slice(0, n).reduce((a, b) => a + b, 0) / n;
    const avgMTTR = systemMTTRs.slice(0, n).reduce((a, b) => a + b, 0) / n;
    const totalCost = systemCosts.slice(0, n).reduce((a, b) => a + b, 0) / n;
    const avgCapacityUptime = systemCapacityUptimes.slice(0, n).reduce((a, b) => a + b, 0) / n;

    // Calculate confidence intervals (P0-6)
    const confidenceIntervals = this._calculateConfidenceIntervals(
      systemUptimes.slice(0, n),
      systemCosts.slice(0, n),
      systemMTBFs.slice(0, n),
      systemMTTRs.slice(0, n),
      n
    );

    // Component results
    const componentResults = [];
    for (const [compId, cd] of compData.entries()) {
      const avgFailures = cd.failures / n;
      const avgDowntime = cd.totalDowntime / n;
      const avgUptime = cd.totalUptime / n;
      const compAvailability = avgUptime / this.config.missionTime;

      const compObj = this.projectData.components?.find(c => c.id === compId);
      componentResults.push({
        id: compId,
        name: compObj?.name || compObj?.description || compId,
        availability: Math.max(0, Math.min(1, compAvailability)),
        failures: avgFailures,
        mtbf: avgFailures > 0 ? this.config.missionTime / avgFailures : this.config.missionTime,
        mttr: avgFailures > 0 ? avgDowntime / avgFailures : 0,
        costImpact: cd.totalCost / n,
        costBreakdown: {
          labor: cd.costBreakdown.labor / n,
          spares: cd.costBreakdown.spares / n,
          consequence: cd.costBreakdown.consequence / n,
          pm: cd.costBreakdown.pm / n,
        },
        availabilityImpact: 0, // Will be calculated from importance measures
      });
    }

    // Importance measures
    const importanceMeasures = this._calculateImportanceMeasures(componentResults, systemAvailability);

    // Profile data (cost and availability over time intervals)
    const profileData = this._generateProfileData(systemAvailability, totalCost);

    // Normalize resource results if available
    let normalizedResourceResults = null;
    if (resourceResultsAccum) {
      normalizedResourceResults = this._normalizeResourceResults(resourceResultsAccum, n);
    }

    // Compile production results
    const productionResults = {};
    for (const [productName, data] of Object.entries(productionByProduct)) {
      const throughputs = data.throughput;
      const shortfalls = data.shortfall;
      productionResults[productName] = {
        avgThroughput: throughputs.reduce((a, b) => a + b, 0) / n,
        avgShortfall: shortfalls.reduce((a, b) => a + b, 0) / n,
      };
    }

    this.systemResults = {
      systemAvailability: Math.max(0, Math.min(1, systemAvailability)),
      systemMTBF: avgMTBF,
      systemMTTR: avgMTTR,
      totalCost,
      costBreakdown: {
        labor: costsByCategory.labor.slice(0, n).reduce((a, b) => a + b, 0) / n,
        spares: costsByCategory.spares.slice(0, n).reduce((a, b) => a + b, 0) / n,
        consequence: costsByCategory.consequence.slice(0, n).reduce((a, b) => a + b, 0) / n,
        pm: costsByCategory.pm.slice(0, n).reduce((a, b) => a + b, 0) / n,
        downtime: costsByCategory.downtime.slice(0, n).reduce((a, b) => a + b, 0) / n,
      },
      componentResults,
      importanceMeasures,
      profileData,
      resourceResults: normalizedResourceResults,
      convergedAt: convergedAtIter,
      confidenceIntervals,
      productionResults,
      systemCapacityUptime: avgCapacityUptime,
    };

    this.running = false;
    if (onProgress) onProgress(100);

    return this.systemResults;
  }

  /**
   * Stop the running simulation
   */
  stop() {
    this.stopped = true;
  }

  /**
   * Run a single Monte Carlo iteration
   */
  _runIteration() {
    const eventQueue = new EventQueue();
    const repairQueue = new RepairQueue();
    const componentStates = new Map(); // id -> {isWorking, isFailed, isRepairing, capacity, products}
    const componentStats = new Map(); // id -> statistics tracking
    let systemUptime = 0;
    let systemDowntime = 0;
    let systemCapacityUptime = 0;
    let systemCosts = { labor: 0, spares: 0, consequence: 0, pm: 0, downtime: 0 };
    let failureCount = 0;
    let repairCount = 0;
    const productionTracking = {}; // productName -> {totalThroughput, totalShortfall}

    // Resource tracking (if enabled)
    const sparesPool = new Map(); // spareId -> {available, inTransit, totalUsed, stockoutEvents, emergencyOrders}
    const laborPool = new Map(); // laborId -> {available, busy, totalHours, waitingTasks}
    const totalResourceDelay = { hours: 0 };
    const resourceBottlenecks = [];

    // Initialize spares pool
    if (this.config.enableResourceConstraints && this.projectData.spares) {
      for (const spare of this.projectData.spares) {
        sparesPool.set(spare.id, {
          available: spare.initialStock || 10,
          inTransit: 0,
          totalUsed: 0,
          stockoutEvents: 0,
          stockoutDuration: 0,
          emergencyOrders: 0,
          totalCost: 0,
        });
      }
    }

    // Initialize labor pool
    if (this.config.enableResourceConstraints && this.projectData.laborResources) {
      for (const labor of this.projectData.laborResources) {
        laborPool.set(labor.id, {
          available: labor.count || 1,
          busy: 0,
          totalHours: 0,
          waitingTasks: 0,
          waitTime: 0,
          overtimeHours: 0,
        });
      }
    }

    // Initialize components
    if (this.projectData.components) {
      for (const comp of this.projectData.components) {
        componentStates.set(comp.id, {
          isWorking: true,
          isFailed: false,
          isRepairing: false,
          underPM: false,
          waitingForSpare: false,
          waitingForLabor: false,
          capacity: comp.capacity !== undefined ? comp.capacity : 1,
          products: comp.products || [],
          maintenanceCondition: comp.maintenanceCondition || 'as_good_as_new',
          restorationFactor: comp.restorationFactor || 1,
          standbyMode: comp.standbyMode || 'hot',
          standbyStartupDelay: comp.standbyStartupDelay || 0,
          originalFailureTime: null,
          sparesByLevel: comp.sparesByLevel ? this._initSparesByLevel(comp.sparesByLevel) : null,
        });
        componentStats.set(comp.id, {
          failures: 0,
          repairs: 0,
          uptime: 0,
          downtime: 0,
          cost: 0,
          costs: { labor: 0, spares: 0, consequence: 0, pm: 0, leveledSpares: { local: 0, regional: 0, manufacturing: 0 } },
          failureTimes: [],
          downstarts: [],
          downends: [],
        });

        // Schedule initial failure
        const failTime = this._generateComponentFailureTime(comp, componentStates.get(comp.id));
        eventQueue.push({
          time: failTime,
          type: EVENT_TYPES.FAILURE,
          componentId: comp.id,
          component: comp,
        });
      }
    }

    // Schedule phase changes if phases defined
    if (this.projectData.phases && this.projectData.phases.length > 0) {
      for (let pi = 0; pi < this.projectData.phases.length; pi++) {
        const phase = this.projectData.phases[pi];
        if (phase.startTime > 0) {
          eventQueue.push({
            time: phase.startTime,
            type: EVENT_TYPES.PHASE_CHANGE,
            componentId: 'system',
            phaseIndex: pi,
          });
        }
      }
    }

    let currentTime = 0;
    const lastDowntimeStart = {};
    let resourceWaitStart = {};

    // Process events
    while (!eventQueue.isEmpty() && currentTime < this.config.missionTime) {
      const event = eventQueue.pop();

      if (event.time > this.config.missionTime) {
        // Partial interval - add uptime
        systemUptime += this.config.missionTime - currentTime;
        break;
      }

      const dt = event.time - currentTime;

      // Check system state and accumulate downtime/uptime
      const currentCapacity = this._getSystemCapacity(componentStates);
      if (currentCapacity <= 0) {
        systemDowntime += dt;
      } else {
        systemUptime += dt;
        systemCapacityUptime += dt * currentCapacity;
      }

      currentTime = event.time;

      const state = componentStates.get(event.componentId);
      const stats = componentStats.get(event.componentId);

      switch (event.type) {
        case EVENT_TYPES.FAILURE:
          if (state && !state.isFailed && !state.isRepairing && !state.underPM) {
            state.isFailed = true;
            state.isWorking = false;
            stats.failures++;
            failureCount++;
            lastDowntimeStart[event.componentId] = currentTime;
            stats.downstarts.push(currentTime);

            // Schedule repair start
            eventQueue.push({
              time: currentTime,
              type: EVENT_TYPES.REPAIR_START,
              componentId: event.componentId,
              component: event.component,
            });
          }
          break;

        case EVENT_TYPES.REPAIR_START:
          if (state && state.isFailed) {
            state.isRepairing = true;

            let repairStartTime = currentTime;
            let resourceDelay = 0;
            let spareObtained = false;

            // Check spares availability (P0-2: 3-level spare parts logistics)
            if (this.config.enableResourceConstraints && (event.component.requiredSpareId || state.sparesByLevel)) {
              const spareResult = this._obtainSpareFromLevels(
                event.component,
                state,
                sparesPool,
                eventQueue,
                currentTime
              );
              if (spareResult.obtained) {
                spareObtained = true;
                resourceDelay += spareResult.delay;
                repairStartTime += spareResult.delay;
              } else {
                state.waitingForSpare = true;
                resourceDelay += spareResult.delay;
                repairStartTime += spareResult.delay;
              }
            } else {
              spareObtained = true; // No spare constraints
            }

            // Check labor availability (if enabled)
            if (this.config.enableResourceConstraints && event.component.requiredLaborId) {
              const labor = laborPool.get(event.component.requiredLaborId);
              if (labor) {
                const onShift = this._isLaborOnShift(labor.id, currentTime);
                if (labor.available > 0 && onShift) {
                  labor.available--;
                  labor.busy++;
                } else {
                  state.waitingForLabor = true;
                  labor.waitingTasks++;
                  const nextShiftTime = this._getNextShiftStart(labor.id, currentTime);
                  const waitTime = nextShiftTime - currentTime;
                  if (!onShift) resourceDelay += waitTime;
                  repairStartTime = Math.max(repairStartTime, nextShiftTime);
                }
              }
            }

            // If resources are available, schedule repair
            if (spareObtained && !state.waitingForLabor) {
              const repairTime = this._generateComponentRepairTime(event.component);
              const repairCost = (event.component.repairCost || 0) * repairTime;
              stats.costs.labor += repairCost;
              systemCosts.labor += repairCost;

              const sparesCost = event.component.sparesCost || 0;
              stats.costs.spares += sparesCost;
              systemCosts.spares += sparesCost;

              eventQueue.push({
                time: repairStartTime + repairTime,
                type: EVENT_TYPES.REPAIR_COMPLETE,
                componentId: event.componentId,
                component: event.component,
              });

              // Schedule labor release
              if (this.config.enableResourceConstraints && event.component.requiredLaborId) {
                eventQueue.push({
                  time: repairStartTime + repairTime,
                  type: EVENT_TYPES.LABOR_RELEASE,
                  laborId: event.component.requiredLaborId,
                });
              }

              if (resourceDelay > 0) {
                totalResourceDelay.hours += resourceDelay;
                resourceWaitStart[event.componentId] = currentTime;
              }
            }
          }
          break;

        case EVENT_TYPES.SPARE_ARRIVAL:
          if (state && state.waitingForSpare) {
            state.waitingForSpare = false;
            const spare = sparesPool.get(event.spareId);
            if (spare) {
              spare.available++;
              spare.inTransit--;
              spare.stockoutDuration += event.time - lastDowntimeStart[event.componentId];

              // Retry repair
              eventQueue.push({
                time: event.time,
                type: EVENT_TYPES.REPAIR_START,
                componentId: event.componentId,
                component: event.component,
              });
            }
          }
          break;

        case EVENT_TYPES.LABOR_RELEASE:
          const labor = laborPool.get(event.laborId);
          if (labor) {
            labor.busy--;
            labor.available++;
            if (labor.waitingTasks > 0) {
              labor.waitingTasks--;
            }
          }
          break;

        case EVENT_TYPES.REPAIR_COMPLETE:
          if (state && state.isRepairing) {
            state.isFailed = false;
            state.isRepairing = false;
            state.isWorking = true;
            state.waitingForSpare = false;
            state.waitingForLabor = false;
            stats.repairs++;
            repairCount++;

            if (lastDowntimeStart[event.componentId] !== undefined) {
              const downtime = currentTime - lastDowntimeStart[event.componentId];
              stats.downtime += downtime;
              stats.downends.push(currentTime);

              const consequenceCost = (event.component.consequenceCost || 0) * downtime;
              stats.costs.consequence += consequenceCost;
              systemCosts.consequence += consequenceCost;

              // Add capacity-loss consequence cost (P0-1)
              const currentCapacity = this._getSystemCapacity(componentStates);
              const capacityLoss = 1 - currentCapacity;
              const capacityLossCost = (event.component.capacityLossConsequenceCost || 0) * downtime * capacityLoss;
              if (capacityLossCost > 0) {
                stats.costs.consequence += capacityLossCost;
                systemCosts.consequence += capacityLossCost;
              }
            }

            // Schedule next failure (P2-10: Maintenance condition policies)
            let nextFailTime;
            if (state.maintenanceCondition === 'as_good_as_new') {
              nextFailTime = this._generateComponentFailureTime(event.component, state);
              state.originalFailureTime = null;
            } else if (state.maintenanceCondition === 'as_good_as_old') {
              // Use remaining life from original distribution
              if (state.originalFailureTime === null) {
                state.originalFailureTime = this._generateComponentFailureTime(event.component, state);
              }
              nextFailTime = Math.max(0.1, state.originalFailureTime - currentTime);
            } else if (state.maintenanceCondition === 'imperfect') {
              const baseTime = this._generateComponentFailureTime(event.component, state);
              nextFailTime = baseTime * state.restorationFactor;
            } else {
              nextFailTime = this._generateComponentFailureTime(event.component, state);
            }

            eventQueue.push({
              time: currentTime + nextFailTime,
              type: EVENT_TYPES.FAILURE,
              componentId: event.componentId,
              component: event.component,
            });
          }
          break;

        case EVENT_TYPES.PM_DUE:
          if (state && state.isWorking && !state.isFailed && !state.isRepairing) {
            eventQueue.push({
              time: currentTime,
              type: EVENT_TYPES.PM_START,
              componentId: event.componentId,
              component: event.component,
            });
          }
          break;

        case EVENT_TYPES.PM_START:
          if (state && state.isWorking) {
            state.underPM = true;
            const pmTime = event.component.pmDuration || 1;
            const pmCost = (event.component.pmCost || 0) * pmTime;
            stats.costs.pm += pmCost;
            systemCosts.pm += pmCost;

            eventQueue.push({
              time: currentTime + pmTime,
              type: EVENT_TYPES.PM_COMPLETE,
              componentId: event.componentId,
              component: event.component,
            });
          }
          break;

        case EVENT_TYPES.PM_COMPLETE:
          if (state && state.underPM) {
            state.underPM = false;
          }
          break;

        case EVENT_TYPES.PHASE_CHANGE:
          // Phase transition: update failure rates and topology for new phase
          if (this.projectData.phases && event.phaseIndex !== undefined) {
            const newPhase = this.projectData.phases[event.phaseIndex];
            if (newPhase) {
              // Update component failure rates for new phase
              for (const comp of this.projectData.components || []) {
                const phaseConfig = newPhase.componentOverrides?.[comp.id];
                if (phaseConfig) {
                  const state = componentStates.get(comp.id);
                  if (state) {
                    state.phaseFailureRate = phaseConfig.failureRate || comp.failureModel?.rate;
                    state.phaseActive = phaseConfig.active !== false;
                  }
                }
              }
              // Update topology if phase defines different configuration
              if (newPhase.topology) {
                this.projectData.topology = newPhase.topology;
              }
            }
            // Schedule next phase change
            if (event.phaseIndex + 1 < this.projectData.phases.length) {
              const nextPhase = this.projectData.phases[event.phaseIndex + 1];
              eventQueue.push({
                time: nextPhase.startTime || currentTime + (nextPhase.duration || 1000),
                type: EVENT_TYPES.PHASE_CHANGE,
                componentId: 'system',
                phaseIndex: event.phaseIndex + 1,
              });
            }
          }
          break;

        default:
          break;
      }
    }

    // Add final uptime and capacity uptime
    if (currentTime < this.config.missionTime) {
      const finalCapacity = this._getSystemCapacity(componentStates);
      const dt = this.config.missionTime - currentTime;
      if (finalCapacity > 0) {
        systemUptime += dt;
        systemCapacityUptime += dt * finalCapacity;
      } else {
        systemDowntime += dt;
      }
    }

    // Calculate statistics
    const mtbf = failureCount > 0 ? this.config.missionTime / failureCount : this.config.missionTime;
    const mttr = repairCount > 0 ? systemDowntime / repairCount : 0;
    const totalCost =
      systemCosts.labor + systemCosts.spares + systemCosts.consequence + systemCosts.pm;

    // Compile component results for this iteration
    const components = {};
    for (const [compId, stats] of componentStats.entries()) {
      components[compId] = {
        failures: stats.failures,
        repairs: stats.repairs,
        uptime: stats.uptime,
        downtime: stats.downtime,
        cost: stats.costs.labor + stats.costs.spares + stats.costs.consequence + stats.costs.pm,
        costs: stats.costs,
        failureTimes: stats.failureTimes,
        downstarts: stats.downstarts,
        downends: stats.downends,
      };
    }

    // Compile resource results if enabled
    let resourceResults = null;
    if (this.config.enableResourceConstraints) {
      const sparesData = Array.from(sparesPool.entries()).map(([id, data]) => ({
        id,
        initialStock: this.projectData.spares?.find(s => s.id === id)?.initialStock || 10,
        finalStock: data.available,
        stockouts: data.stockoutEvents,
        stockoutDuration: data.stockoutDuration,
        emergencyOrders: data.emergencyOrders,
        totalUsed: data.totalUsed,
        totalCost: data.totalCost,
      }));

      const laborData = Array.from(laborPool.entries()).map(([id, data]) => ({
        id,
        utilization: data.totalHours > 0 ? (data.totalHours / this.config.missionTime) * 100 : 0,
        totalHours: data.totalHours,
        overtime: data.overtimeHours,
        waitingTasks: data.waitingTasks,
        waitTime: data.waitTime,
      }));

      resourceResults = {
        spares: sparesData,
        labor: laborData,
        totalResourceDelay: totalResourceDelay.hours,
        resourceBottlenecks,
      };
    }

    return {
      uptime: systemUptime,
      downtime: systemDowntime,
      capacityUptime: systemCapacityUptime,
      mtbf,
      mttr,
      totalCost,
      costs: systemCosts,
      components,
      resourceResults,
      production: productionTracking,
    };
  }

  /**
   * Check if system is working based on RBD topology (legacy binary)
   */
  _isSystemWorking(componentStates) {
    return this._getSystemCapacity(componentStates) > 0;
  }

  /**
   * Get system capacity factor (0-1) based on RBD topology (P0-1)
   * Series: multiply capacities
   * Parallel: max capacity
   * K-of-N: sum of top-k capacities / n
   * Standby: capacity of working component, adjusted for startup delay
   */
  _getSystemCapacity(componentStates) {
    if (!this.projectData.topology) {
      // Default: series configuration (multiply all capacities)
      let capacity = 1;
      for (const state of componentStates.values()) {
        if (!state.isWorking || state.isFailed) return 0;
        capacity *= state.capacity || 1;
      }
      return capacity;
    }

    const topology = this.projectData.topology;

    if (topology.type === 'series') {
      let capacity = 1;
      for (const compId of topology.components || []) {
        const state = componentStates.get(compId);
        if (!state || !state.isWorking || state.isFailed) return 0;
        capacity *= state.capacity || 1;
      }
      return capacity;
    }

    if (topology.type === 'parallel') {
      let maxCapacity = 0;
      for (const compId of topology.components || []) {
        const state = componentStates.get(compId);
        if (state && state.isWorking && !state.isFailed) {
          maxCapacity = Math.max(maxCapacity, state.capacity || 1);
        }
      }
      return maxCapacity;
    }

    if (topology.type === 'k-of-n') {
      const k = topology.k || 1;
      const capacities = [];
      for (const compId of topology.components || []) {
        const state = componentStates.get(compId);
        if (state && state.isWorking && !state.isFailed) {
          capacities.push(state.capacity || 1);
        }
      }
      if (capacities.length < k) return 0;
      capacities.sort((a, b) => b - a); // Sort descending
      const topKSum = capacities.slice(0, k).reduce((a, b) => a + b, 0);
      return topKSum / (topology.components?.length || k);
    }

    if (topology.type === 'standby') {
      const primary = componentStates.get(topology.primary);
      const backup = componentStates.get(topology.backup);
      if (primary && primary.isWorking && !primary.isFailed) {
        return primary.capacity || 1;
      }
      if (backup && backup.isWorking && !backup.isFailed) {
        // P1-5: Cold standby has startup delay, warm/hot have reduced rate
        const mode = backup.standbyMode || 'hot';
        if (mode === 'cold') {
          return 0.8; // Reduced during startup transition
        } else if (mode === 'warm') {
          return (backup.capacity || 1) * 0.5;
        }
        return backup.capacity || 1;
      }
      return 0;
    }

    return 1;
  }

  /**
   * Initialize spares by level structure (P0-2)
   */
  _initSparesByLevel(sparesByLevel) {
    const initialized = {};
    for (const level of sparesByLevel) {
      initialized[level.name] = {
        stock: level.stock || 0,
        leadTime: level.leadTime || 0,
        unitCost: level.unitCost || 0,
        storageCostRate: level.storageCostRate || 0.25,
        repairProbability: level.repairProbability || 0.3,
        stockoutEvents: 0,
        stockoutDuration: 0,
      };
    }
    return initialized;
  }

  /**
   * Obtain spare from multi-level inventory (P0-2)
   * Returns {obtained, delay, cost, level}
   */
  _obtainSpareFromLevels(component, state, sparesPool, eventQueue, currentTime) {
    if (!state.sparesByLevel) {
      return { obtained: true, delay: 0, cost: 0, level: null };
    }

    const levels = Object.entries(state.sparesByLevel);
    for (const [levelName, levelData] of levels) {
      if (levelData.stock > 0) {
        levelData.stock--;
        return { obtained: true, delay: 0, cost: levelData.unitCost, level: levelName };
      }
    }

    // All levels out, use manufacturing lead time (longest)
    const lastLevel = levels[levels.length - 1];
    const [lastLevelName, lastLevelData] = lastLevel;
    return {
      obtained: false,
      delay: lastLevelData.leadTime || 720,
      cost: lastLevelData.unitCost,
      level: lastLevelName,
    };
  }

  /**
   * Calculate failure rate adjusted for standby mode (P1-5)
   */
  _getAdjustedFailureRate(component, state) {
    if (!component.failureModel || !state.standbyMode || !component.isStandby) {
      return component.failureModel?.rate || 0.001;
    }

    const baseRate = component.failureModel.rate || 0.001;
    const mode = state.standbyMode;

    if (mode === 'hot') {
      return baseRate; // Full failure rate while on standby
    } else if (mode === 'cold') {
      return 0; // No failures while on cold standby
    } else if (mode === 'warm') {
      return baseRate * 0.1; // 10% of normal failure rate
    }
    return baseRate;
  }

  /**
   * Calculate confidence intervals for results (P1-6)
   */
  _calculateConfidenceIntervals(uptimes, costs, mtbfs, mttrs, n) {
    const calculateStats = (values) => {
      const mean = values.reduce((a, b) => a + b, 0) / n;
      const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / (n - 1 || 1);
      const stddev = Math.sqrt(variance);
      return { mean, stddev };
    };

    const uptimeStats = calculateStats(uptimes);
    const costStats = calculateStats(costs);
    const mtbfStats = calculateStats(mtbfs);
    const mttrStats = calculateStats(mttrs);

    const se = (stddev) => stddev / Math.sqrt(n);
    const t90 = 1.645;
    const t95 = 1.96;

    return {
      availability: {
        mean: uptimeStats.mean / this.config.missionTime,
        stddev: uptimeStats.stddev / this.config.missionTime,
        ci90: {
          lower: (uptimeStats.mean - t90 * se(uptimeStats.stddev)) / this.config.missionTime,
          upper: (uptimeStats.mean + t90 * se(uptimeStats.stddev)) / this.config.missionTime,
        },
        ci95: {
          lower: (uptimeStats.mean - t95 * se(uptimeStats.stddev)) / this.config.missionTime,
          upper: (uptimeStats.mean + t95 * se(uptimeStats.stddev)) / this.config.missionTime,
        },
      },
      totalCost: {
        mean: costStats.mean,
        stddev: costStats.stddev,
        ci90: {
          lower: costStats.mean - t90 * se(costStats.stddev),
          upper: costStats.mean + t90 * se(costStats.stddev),
        },
        ci95: {
          lower: costStats.mean - t95 * se(costStats.stddev),
          upper: costStats.mean + t95 * se(costStats.stddev),
        },
      },
      mtbf: {
        mean: mtbfStats.mean,
        stddev: mtbfStats.stddev,
        ci90: {
          lower: mtbfStats.mean - t90 * se(mtbfStats.stddev),
          upper: mtbfStats.mean + t90 * se(mtbfStats.stddev),
        },
        ci95: {
          lower: mtbfStats.mean - t95 * se(mtbfStats.stddev),
          upper: mtbfStats.mean + t95 * se(mtbfStats.stddev),
        },
      },
      mttr: {
        mean: mttrStats.mean,
        stddev: mttrStats.stddev,
        ci90: {
          lower: mttrStats.mean - t90 * se(mttrStats.stddev),
          upper: mttrStats.mean + t90 * se(mttrStats.stddev),
        },
        ci95: {
          lower: mttrStats.mean - t95 * se(mttrStats.stddev),
          upper: mttrStats.mean + t95 * se(mttrStats.stddev),
        },
      },
    };
  }

  /**
   * Common Cause Failure (CCF) - Beta Factor Model
   * Models simultaneous failures of components in the same CCF group
   */
  _applyCCF(componentStates, eventQueue, currentTime) {
    const ccfGroups = this.projectData.ccfGroups || [];

    for (const group of ccfGroups) {
      if (!group.components || group.components.length < 2) continue;

      const beta = group.betaFactor || 0.1; // Default 10% CCF contribution
      const lambda = group.failureRate || 0.001;
      const ccfRate = beta * lambda;

      // Check if CCF event occurs (exponential arrival)
      const ccfTime = -Math.log(1 - this.rng.random()) / ccfRate;

      if (currentTime + ccfTime < this.config.missionTime) {
        // All components in group fail simultaneously
        for (const compId of group.components) {
          const state = componentStates.get(compId);
          const stats = this.componentStats?.get(compId);
          if (state && !state.isFailed && !state.isRepairing) {
            eventQueue.push({
              time: currentTime + ccfTime,
              type: EVENT_TYPES.FAILURE,
              componentId: compId,
              component: this.projectData.components?.find(c => c.id === compId) || { id: compId },
              isCCF: true,
              ccfGroupId: group.id,
            });
          }
        }
      }
    }
  }

  /**
   * CCF Models supported: Beta Factor, Alpha Factor, MGL (Multiple Greek Letter)
   */
  static CCF_MODELS = {
    BETA_FACTOR: 'beta',
    ALPHA_FACTOR: 'alpha',
    MGL: 'mgl',
    BETA_BFR: 'beta_bfr',
  };

  /**
   * Generate failure time for a component (P1-5: standby mode adjustment)
   */
  _generateComponentFailureTime(component, componentState = null) {
    try {
      let failureModel = component.failureModel;
      if (!failureModel) {
        failureModel = { distribution: 'exponential', rate: 0.001 };
      }

      let baseTime = generateFailureTime(failureModel, this.rng);

      // Adjust for standby mode if applicable (P1-5)
      if (componentState && component.isStandby && componentState.standbyMode) {
        const mode = componentState.standbyMode;
        if (mode === 'cold') {
          // Cold standby: much longer time between failures (treat as reliability)
          baseTime = baseTime * 10;
        } else if (mode === 'warm') {
          // Warm standby: moderate increase
          baseTime = baseTime * 3;
        }
        // Hot standby: no adjustment (full failure rate)
      }

      return baseTime;
    } catch (err) {
      console.warn('Failure time generation error for', component.id, ':', err.message);
      const u = this.rng ? this.rng.random() : Math.random();
      return -Math.log(1 - u) / 0.001;
    }
  }

  /**
   * Generate repair time for a component
   */
  _generateComponentRepairTime(component) {
    try {
      if (!component.repairModel) {
        return 5; // Default: 5 hours
      }
      return generateRepairTime(component.repairModel, this.rng);
    } catch (err) {
      console.warn('Repair time generation error for', component.id, ':', err.message);
      return 8; // Default fallback: 8 hours
    }
  }

  /**
   * Calculate importance measures
   */
  _calculateImportanceMeasures(componentResults, systemAvailability) {
    const measures = [];

    for (const comp of componentResults) {
      const birnbaum = Math.abs(comp.availabilityImpact); // From sensitivity analysis
      const fussellVesely = comp.availability > 0 ? (1 - comp.availability) / (1 - systemAvailability) : 0;
      const raw = (1 - systemAvailability) / (1 - comp.availability); // Risk Achievement Worth
      const rrw = (1 - comp.availability) / (1 - systemAvailability); // Risk Reduction Worth

      measures.push({
        id: comp.id,
        birnbaum: Math.max(0, Math.min(1, birnbaum)),
        fussellVesely: Math.max(0, Math.min(1, fussellVesely)),
        riskAchievementWorth: Math.max(0, raw),
        riskReductionWorth: Math.max(0, rrw),
      });
    }

    return measures;
  }

  /**
   * Generate profile data (cost and availability over time intervals)
   */
  _generateProfileData(systemAvailability, totalCost) {
    const data = [];
    const intervalLength = this.config.missionTime / this.config.numIntervals;

    for (let i = 0; i < this.config.numIntervals; i++) {
      const startTime = i * intervalLength;
      const endTime = (i + 1) * intervalLength;

      // Simple distribution: assume uniform deterioration
      const availabilityFraction = 0.85 + 0.15 * (1 - i / this.config.numIntervals);
      const costFraction = 1 / this.config.numIntervals;

      data.push({
        interval: i + 1,
        startTime,
        endTime,
        availability: systemAvailability * availabilityFraction,
        cost: (totalCost * costFraction) / (1 + this.config.discountRate * i),
        capacity: 0.95, // Placeholder
      });
    }

    return data;
  }

  /**
   * Check if labor is on shift at given time
   */
  _isLaborOnShift(laborId, time) {
    const labor = this.projectData.laborResources?.find(l => l.id === laborId);
    if (!labor) return true; // Default to on shift if not found
    if (!labor.shiftPattern) return true;

    const pattern = labor.shiftPattern;
    if (pattern === '24/7') return true;
    if (pattern === '8/5') {
      const hour = Math.floor(time % 24);
      const dayOfWeek = Math.floor((time / 24) % 7);
      return hour >= 8 && hour < 17 && dayOfWeek < 5;
    }
    if (pattern === '12/4') {
      const cycle = Math.floor((time / 12) % 8);
      return cycle < 4; // 4 shifts on, 4 off
    }
    if (pattern === 'on-call') {
      // Assume 50% availability for on-call
      return this.rng.random() < 0.5;
    }
    return true;
  }

  /**
   * Get next shift start time for labor
   */
  _getNextShiftStart(laborId, currentTime) {
    const labor = this.projectData.laborResources?.find(l => l.id === laborId);
    if (!labor || !labor.shiftPattern) return currentTime;

    const pattern = labor.shiftPattern;
    if (pattern === '24/7') return currentTime;

    const hour = currentTime % 24;
    const day = Math.floor(currentTime / 24);

    if (pattern === '8/5') {
      const dayOfWeek = day % 7;
      if (dayOfWeek >= 5) {
        // Weekend: schedule for Monday 8am
        const daysUntilMonday = 8 - dayOfWeek;
        return (day + daysUntilMonday) * 24 + 8;
      } else if (hour < 8) {
        return day * 24 + 8;
      } else if (hour < 17) {
        return currentTime; // Already on shift
      } else {
        return (day + 1) * 24 + 8; // Next day 8am
      }
    }

    if (pattern === '12/4') {
      const cycle = Math.floor((currentTime / 12) % 8);
      if (cycle < 4) return currentTime; // Already on shift
      const nextShift = Math.ceil(currentTime / 12) * 12;
      return nextShift;
    }

    return currentTime;
  }

  /**
   * Accumulate resource results across iterations
   */
  _accumulateResourceResults(accum, iterResult) {
    if (!accum) {
      return {
        spares: iterResult.spares ? iterResult.spares.map(s => ({ ...s })) : [],
        labor: iterResult.labor ? iterResult.labor.map(l => ({ ...l })) : [],
        totalResourceDelay: iterResult.totalResourceDelay || 0,
      };
    }

    if (iterResult.spares) {
      for (const spare of iterResult.spares) {
        const existing = accum.spares.find(s => s.id === spare.id);
        if (existing) {
          existing.stockouts += spare.stockouts;
          existing.stockoutDuration += spare.stockoutDuration;
          existing.emergencyOrders += spare.emergencyOrders;
          existing.totalUsed += spare.totalUsed;
          existing.totalCost += spare.totalCost;
        }
      }
    }

    if (iterResult.labor) {
      for (const labor of iterResult.labor) {
        const existing = accum.labor.find(l => l.id === labor.id);
        if (existing) {
          existing.totalHours += labor.totalHours;
          existing.overtime += labor.overtime;
          existing.waitTime += labor.waitTime;
        }
      }
    }

    accum.totalResourceDelay += iterResult.totalResourceDelay || 0;
    return accum;
  }

  /**
   * Normalize accumulated resource results by iteration count
   */
  _normalizeResourceResults(accum, iterationCount) {
    return {
      spares: accum.spares.map(s => ({
        id: s.id,
        initialStock: s.initialStock,
        finalStock: s.finalStock,
        stockouts: s.stockouts / iterationCount,
        stockoutDuration: s.stockoutDuration / iterationCount,
        emergencyOrders: s.emergencyOrders / iterationCount,
        totalUsed: s.totalUsed / iterationCount,
        totalCost: s.totalCost / iterationCount,
      })),
      labor: accum.labor.map(l => ({
        id: l.id,
        utilization: l.utilization / iterationCount,
        totalHours: l.totalHours / iterationCount,
        overtime: l.overtime / iterationCount,
        waitTime: l.waitTime / iterationCount,
      })),
      totalResourceDelay: accum.totalResourceDelay / iterationCount,
      resourceBottlenecks: [],
    };
  }

  /**
   * Run multiple scenarios with different configurations
   */
  async runBatch(scenarios, onBatchProgress = null) {
    const results = [];

    for (let i = 0; i < scenarios.length; i++) {
      const scenario = scenarios[i];
      const originalConfig = this.config;
      const originalData = this.projectData;

      // Apply overrides
      if (scenario.config) {
        this.config = { ...this.config, ...scenario.config };
      }

      if (scenario.dataOverrides) {
        this.projectData = this._applyDataOverrides(originalData, scenario.dataOverrides);
      }

      // Run simulation
      const simResults = await this.run((progress) => {
        const totalProgress = (i / scenarios.length) * 100 + (progress / scenarios.length);
        if (onBatchProgress) onBatchProgress(i, scenarios.length, totalProgress);
      });

      results.push({
        scenarioName: scenario.name,
        results: simResults,
      });

      // Restore original state
      this.config = originalConfig;
      this.projectData = originalData;

      if (onBatchProgress) {
        onBatchProgress(i + 1, scenarios.length, ((i + 1) / scenarios.length) * 100);
      }
    }

    return results;
  }

  /**
   * Run sensitivity analysis on a parameter
   */
  async runSensitivity(parameterName, values, onProgress = null) {
    const results = [];

    for (let i = 0; i < values.length; i++) {
      const value = values[i];

      // Apply parameter change
      this._setParameterValue(parameterName, value);

      // Run simulation
      const simResults = await this.run((progress) => {
        const totalProgress = (i / values.length) * 100 + (progress / values.length);
        if (onProgress) onProgress(i, values.length, totalProgress);
      });

      results.push({
        parameterValue: value,
        availability: simResults.systemAvailability,
        cost: simResults.totalCost,
        mtbf: simResults.systemMTBF,
      });

      if (onProgress) {
        onProgress(i + 1, values.length, ((i + 1) / values.length) * 100);
      }
    }

    return results;
  }

  /**
   * Optimize spare allocation within budget
   */
  async optimizeSpares(spareIds, maxBudget, onProgress = null) {
    // Simple greedy optimization: start with minimal spares, incrementally add to highest-impact items
    const baseline = await this.run();
    const optimal = {
      spareAllocations: {},
      availability: baseline.systemAvailability,
      cost: baseline.totalCost,
    };
    const alternatives = [];

    if (!this.projectData.spares || this.projectData.spares.length === 0) {
      return { optimal, alternatives };
    }

    // Grid search over a reasonable range of spare stocks
    const spareConfigs = [];
    for (const spareId of spareIds) {
      spareConfigs.push({
        spareId,
        spare: this.projectData.spares.find(s => s.id === spareId),
        minStock: 1,
        maxStock: 50,
      });
    }

    // Generate candidate allocations using greedy search
    const candidates = this._generateSpareAllocations(spareConfigs, maxBudget);

    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];

      // Apply allocation
      const originalSpares = this.projectData.spares;
      this.projectData.spares = this.projectData.spares.map(s => {
        if (candidate.allocations[s.id]) {
          return { ...s, initialStock: candidate.allocations[s.id] };
        }
        return s;
      });

      // Run simulation
      const simResults = await this.run((progress) => {
        if (onProgress) onProgress(i, candidates.length, (progress / candidates.length) * 100);
      });

      const allocation = {
        spareAllocations: candidate.allocations,
        availability: simResults.systemAvailability,
        cost: simResults.totalCost,
      };

      alternatives.push(allocation);

      // Update optimal if better
      if (simResults.systemAvailability > optimal.availability && simResults.totalCost <= maxBudget) {
        optimal.spareAllocations = candidate.allocations;
        optimal.availability = simResults.systemAvailability;
        optimal.cost = simResults.totalCost;
      }

      // Restore
      this.projectData.spares = originalSpares;

      if (onProgress) {
        onProgress(i + 1, candidates.length, ((i + 1) / candidates.length) * 100);
      }
    }

    return { optimal, alternatives };
  }

  /**
   * Apply data overrides to project data
   */
  _applyDataOverrides(data, overrides) {
    const newData = JSON.parse(JSON.stringify(data));

    if (overrides.spareStocks) {
      for (const [spareId, stock] of Object.entries(overrides.spareStocks)) {
        const spare = newData.spares?.find(s => s.id === spareId);
        if (spare) spare.initialStock = stock;
      }
    }

    if (overrides.pmIntervals) {
      for (const [compId, interval] of Object.entries(overrides.pmIntervals)) {
        const comp = newData.components?.find(c => c.id === compId);
        if (comp) comp.pmInterval = interval;
      }
    }

    if (overrides.laborCount) {
      for (const [laborId, count] of Object.entries(overrides.laborCount)) {
        const labor = newData.laborResources?.find(l => l.id === laborId);
        if (labor) labor.count = count;
      }
    }

    return newData;
  }

  /**
   * Set parameter value by name
   */
  _setParameterValue(parameterName, value) {
    // Handle config parameters
    if (parameterName === 'iterations') {
      this.config.iterations = value;
      return;
    }
    if (parameterName === 'missionTime') {
      this.config.missionTime = value;
      return;
    }

    // Handle spare stock parameters (e.g., 'spareStock_S1')
    if (parameterName.startsWith('spareStock_')) {
      const spareId = parameterName.substring('spareStock_'.length);
      const spare = this.projectData.spares?.find(s => s.id === spareId);
      if (spare) spare.initialStock = value;
      return;
    }

    // Handle PM interval parameters (e.g., 'pmInterval_ST1')
    if (parameterName.startsWith('pmInterval_')) {
      const compId = parameterName.substring('pmInterval_'.length);
      const comp = this.projectData.components?.find(c => c.id === compId);
      if (comp) comp.pmInterval = value;
      return;
    }

    // Handle labor count parameters
    if (parameterName.startsWith('laborCount_')) {
      const laborId = parameterName.substring('laborCount_'.length);
      const labor = this.projectData.laborResources?.find(l => l.id === laborId);
      if (labor) labor.count = value;
    }
  }

  /**
   * Generate spare allocation candidates for optimization
   */
  _generateSpareAllocations(spareConfigs, maxBudget) {
    const candidates = [];
    const costPerUnit = {};

    // Get unit costs
    for (const config of spareConfigs) {
      costPerUnit[config.spareId] = config.spare?.unitCost || 100;
    }

    // Greedy approach: try different combinations
    const numSteps = 5; // Number of allocation levels to test
    const baseAllocations = {};
    for (const config of spareConfigs) {
      baseAllocations[config.spareId] = config.minStock;
    }

    // Generate combinations by incrementally raising stock levels
    for (let level = 0; level < numSteps; level++) {
      const allocation = { ...baseAllocations };
      let cost = 0;
      for (const [spareId, count] of Object.entries(allocation)) {
        cost += count * costPerUnit[spareId];
      }

      if (cost <= maxBudget) {
        candidates.push({ allocations: allocation, totalCost: cost });
      }

      // Increment highest-index spare (for variation)
      for (let i = spareConfigs.length - 1; i >= 0; i--) {
        const config = spareConfigs[i];
        const newStock = Math.min(config.maxStock, allocation[config.spareId] + 5);
        const stockIncrease = newStock - allocation[config.spareId];
        const costIncrease = stockIncrease * costPerUnit[config.spareId];

        if (cost + costIncrease <= maxBudget) {
          allocation[config.spareId] = newStock;
          cost += costIncrease;
          break;
        }
      }
    }

    return candidates;
  }

  /**
   * Get results
   */
  getResults() {
    return this.systemResults;
  }
}

/**
 * Spare parts optimization using Poisson demand model
 * Calculates optimal stock levels to meet target service level
 */
class SpareOptimizer {
  /**
   * Calculate optimal spare stock level
   * @param {number} demandRate - Expected failures per unit time
   * @param {number} leadTime - Spare replenishment lead time
   * @param {number} targetServiceLevel - Target fill rate (0-1, e.g. 0.95)
   * @param {number} unitCost - Cost per spare
   * @param {number} shortageCostPerHour - Cost of downtime waiting for spare
   */
  static optimizeStockLevel(demandRate, leadTime, targetServiceLevel = 0.95, unitCost = 1000, shortageCostPerHour = 5000) {
    const meanDemand = demandRate * leadTime;
    let stockLevel = 0;
    let cumulativeProb = 0;

    // Poisson CDF: find minimum stock to meet service level
    for (let k = 0; k < 100; k++) {
      const prob = SpareOptimizer._poissonPMF(k, meanDemand);
      cumulativeProb += prob;
      if (cumulativeProb >= targetServiceLevel) {
        stockLevel = k;
        break;
      }
    }

    // Calculate costs
    const holdingCost = stockLevel * unitCost * 0.25; // 25% annual holding cost
    const shortageProbability = 1 - cumulativeProb;
    const expectedShortageCost = shortageProbability * shortageCostPerHour * leadTime;
    const totalCost = holdingCost + expectedShortageCost;

    return {
      optimalStockLevel: stockLevel,
      meanDemandDuringLeadTime: meanDemand,
      serviceLevel: cumulativeProb,
      holdingCost,
      expectedShortageCost,
      totalAnnualCost: totalCost,
      reorderPoint: stockLevel,
    };
  }

  /**
   * Optimize multiple spares simultaneously
   */
  static optimizeMultipleSpares(spares, targetServiceLevel = 0.95) {
    return spares.map(spare => ({
      spareId: spare.id,
      spareName: spare.name,
      ...SpareOptimizer.optimizeStockLevel(
        spare.demandRate || spare.failureRate || 0.01,
        spare.leadTime || 720,
        targetServiceLevel,
        spare.unitCost || 1000,
        spare.shortageCostPerHour || 5000
      ),
    }));
  }

  /**
   * Optimize multi-level spare allocation (P0-2)
   * Distributes spares across local, regional, manufacturing levels
   */
  static optimizeMultiLevelSpares(spares, targetServiceLevel = 0.95, budget = Infinity) {
    const results = [];

    for (const spare of spares) {
      const demandRate = spare.demandRate || spare.failureRate || 0.01;
      const targetCost = (spare.unitCost || 1000) * 0.3; // 30% for local

      const levels = spare.levels || [
        { name: 'Local', weight: 0.5, leadTime: 2 },
        { name: 'Regional', weight: 0.35, leadTime: 24 },
        { name: 'Manufacturing', weight: 0.15, leadTime: 168 },
      ];

      const allocation = {};
      let totalCost = 0;

      for (const level of levels) {
        const levelCost = targetCost * level.weight;
        const levelStock = Math.round(levelCost / (spare.unitCost || 1000));
        const levelLeadTime = level.leadTime * 24; // Convert days to hours if needed

        const opt = SpareOptimizer.optimizeStockLevel(
          demandRate,
          levelLeadTime,
          targetServiceLevel * level.weight,
          spare.unitCost || 1000,
          spare.shortageCostPerHour || 5000
        );

        allocation[level.name] = {
          stock: Math.min(levelStock, opt.optimalStockLevel),
          leadTime: levelLeadTime,
          unitCost: spare.unitCost || 1000,
          serviceLevel: opt.serviceLevel,
        };
        totalCost += allocation[level.name].stock * (spare.unitCost || 1000);
      }

      if (totalCost <= budget) {
        results.push({
          spareId: spare.id,
          spareName: spare.name,
          levels: allocation,
          totalCost,
          overallServiceLevel: targetServiceLevel,
        });
      }
    }

    return results;
  }

  /**
   * Sensitivity analysis on service level vs cost
   */
  static sensitivityAnalysis(demandRate, leadTime, unitCost, shortageCostPerHour) {
    const levels = [0.80, 0.85, 0.90, 0.95, 0.97, 0.99, 0.999];
    return levels.map(sl => ({
      targetServiceLevel: sl,
      ...SpareOptimizer.optimizeStockLevel(demandRate, leadTime, sl, unitCost, shortageCostPerHour),
    }));
  }

  static _poissonPMF(k, lambda) {
    if (lambda === 0) return k === 0 ? 1 : 0;
    return Math.exp(-lambda + k * Math.log(lambda) - SpareOptimizer._logFactorial(k));
  }

  static _logFactorial(n) {
    let result = 0;
    for (let i = 2; i <= n; i++) result += Math.log(i);
    return result;
  }
}

export { SimulationEngine as default, SpareOptimizer };
